import {useEffect, useState, useRef, useMemo, useCallback} from "react";

import { AgGridReact } from "ag-grid-react";
import {
  Box,
  FormControl,
  TextField,
  InputAdornment,
  Typography,
  Divider,
  CircularProgress,
  ToggleButtonGroup
} from "@mui/material";
import { StyledToggleButton } from "@plannel/components/ui/ToggleButton";
import {
  BarChart,
  Bar,
  Cell,
  // Brush,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { green } from "@mui/material/colors";
import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { size, isEmpty, isEqual, isUndefined } from "lodash";
import DpVersionSelect from "@plannel/pages/demand-plan/dp-workbench/DpVersionSelect";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter"
import PaginationContainer from "@plannel/components/PaginationContainer"
import { DPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import debug from "@plannel/utils/debug-util";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtil from "@plannel/pages/utils/code-util";
import bfLeaderboardService from "@plannel/services/dp/bf-leaderboard-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import measureService from "@plannel/services/data/measure-service";
import SplitterContainer from "@plannel/components/SplitterContainer";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import colorUtil from "@plannel/pages/utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import FilterStatusPanel, { getFilterStore, extractFilterInfo } from  "@plannel/components/aggrid/FilterStatusPanel";
import {FilterButton} from "@plannel/components/ActionIconButton";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import { FS } from "@plannel/utils/font-util";

const MIN_SALES_PERIOD = 1;
const DEFAULT_METRIC = "WAPE";
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const HISTORY = "history";
const HISTOGRAM_BEST = "histogram_best";


const BfLeaderboard = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const { colorSet, snackBarMessageColor, algorithmColor } = colorUtil;
  const { toggleType, customer, item, bfAlgorithm } = codeUtil;
  const [chartToggle, setChartToggle] = useState(reduxViewState?.chartToggle ? reduxViewState.chartToggle : HISTORY);

  const [leaderboard, setLeaderboard] = useState([]);
  const [bestModelCnt, setBestModelCnt] = useState([]);
  const [chart1Data, setChart1Data] = useState([]);
  const [chart1ModelCds, setChart1ModelCds] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [forecastError, setForecastError] = useState([]);
  const [selectedBarIndex, setSelectedBarIndex] = useState(null);
  const [brushValue, setBrushValue] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [merticColDef, setMetricColDef] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [posActiveFlg, setPosActiveFlg] = useState(false);
  const algorithmType = Object.values(bfAlgorithm).map(v => v.name);
  const [metric, setMetric] = useState("");
  const metricRef = useRef("");
  // 번역 전 구성. 시리즈 텍스트를 state 에 굳히면 언어를 바꿔도 차트가 옛 언어로 남는다.
  const [chartSeriesSpec, setChartSeriesSpec] = useState({ modelCds: [], chartType: toggleType.BAR });
  const [displayNames, setDisplayNames] = useState({});

  const chartCellRenderer = (value) => {
    return displayNames?.[value] || null;
  };

  const chartSeries = useMemo(() => {
    const salesActualQtyName = posActiveFlg ? "posSalesActualQty" : "salesActualQty";
    const chart = [{
      field: 'qty',
      text: chartCellRenderer(salesActualQtyName) ?? t(salesActualQtyName),
      type: toggleType.LINE,
      typeOptions: { fill: colorSet(0) },
      orientation: "left",
      groupName: t("qty"),
    }];

    // 모델 코드는 서버 값이라 번역 대상이 아니다.
    for (const modelCd of chartSeriesSpec.modelCds) {
      chart.push({
        field: modelCd,
        text: modelCd,
        type: chartSeriesSpec.chartType,
        typeOptions: { strokeWidth: 3, fill: algorithmColor[modelCd] },
        orientation: "left",
        groupName: t("qty"),
        backgroundColor: "#eee"
      });
    }

    return chart;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSeriesSpec, t, posActiveFlg, displayNames]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [selectVersion, setSelectVersion] = useState(reduxViewState?.selectVersion ?? {});
  const [vListReload, setVListReload] = useState(false);

  const [selectCL] = useState(reduxViewState?.custLevelCd ?? "");
  const [selectIL] = useState(reduxViewState?.itemLevelCd ?? "");

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});

  const [salesPeriod, setSalesPeriod] = useState(reduxViewState?.salesPeriod ? reduxViewState.salesPeriod : 12);
  const [defaultValue] = useState(reduxViewState?.filter?.customerHrchys ?? []);
  const [itemHrchyDefaultValue] = useState(reduxViewState?.filter?.category ?? []);

  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const maxSalesPeriod = useRef(36);

  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }))

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    defaultSettings.defaultColDef = {
      flex: 1,
      minWidth: 100,
    }
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearChart = () => {
    setChartData([]);
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const makeMetricColDef = (bestMetric) => {
    let orderBy = [];
    if (!isUndefined(bestMetric) && bestMetric !== "") {
      orderBy.push(bestMetric);
      for (const metric of codeUtil.bfMetric) {
        if (metric !== bestMetric) {
          orderBy.push(metric);
        }
      }
    } else {
      orderBy = codeUtil.bfMetric;
    }

    const colDef = [];
    for (const metric of orderBy) {
      const col = {
        headerName: metric,
        field: metric.toLowerCase(),
        hide: false,
        sortable: false,
        suppressColumnsToolPanel: true,
        rowGroup: false
      }
      colDef.push(col);
    }
    setMetricColDef(colDef);
  }

  const retrieveParameters = () => {
    setChart1Data([]);
    if (selectVersion && selectVersion.planConfigObj?.BF_SETTING) {
      setMetric(selectVersion.planConfigObj?.BF_SETTING?.bestSelection?.metrics)
      metricRef.current = selectVersion.planConfigObj?.BF_SETTING?.bestSelection?.metrics;
    } else {
      const param = {
        moduleCd: "DP",
        configCd: codeUtil.dpConfig.BF_PARAMETER,
        activeFlg: true
      };

      let bestMetric = DEFAULT_METRIC;
      planConfigService.getConfigCd(param)
        .then(response => {
          if (response.data) {
            bestMetric = response.data.objVal?.bestSelection?.metrics
          }
          setMetric(bestMetric)
          metricRef.current = bestMetric;
        })
        .catch(e => {
          debug(e);
        });
    }
  }

  const getLevelCdOnParams = (isCust) => {
    const filter = isCust ? filterValue.customerHrchys : filterValue.category;
    const selectLvCd = isCust ? selectCL : selectIL;
    const categoryfilterList = filter?.map(data => data?.hrchyCd);

    return size(categoryfilterList) > 0 ? filter[0]?.hrchyLv : selectLvCd;
  }

  const setChartSeriesData = (chartData, modelCds, chartType) => {
    setChartSeriesSpec({ modelCds, chartType });
    setChartData(chartData);
  }


  // z_measure(measureService.getAll())의 실제 표시명(name)을 measureCd 기준으로 codeUtil.dpMeasure의
  // 식별자(.name)에 매핑한다. 차트 텍스트는 이 식별자를 그대로 써야 하므로 displayNames는 별도
  // state로만 두고 데이터 필드에는 절대 반영하지 않는다.
  const retrieveMeasureDisplayName = () => {
    measureService.getAll({isBasic: true})
      .then(response => {
        const results = response?.data?.results;
        if (isEmpty(results)) {
          return;
        }
        const nameByMeasureCd = {};
        for (const measure of results) {
          nameByMeasureCd[measure.measureCd] = measure.name;
        }

        const nextDisplayNames = {};
        for (const measure of Object.values(codeUtil.dpMeasure)) {
          const zMeasureName = nameByMeasureCd[measure.cd];
          if (zMeasureName && !isEqual(zMeasureName, measure.name)) {
            nextDisplayNames[measure.name] = zMeasureName;
          }
        }
        setDisplayNames(nextDisplayNames);
      })
      .catch(e => {
        debug(e);
      });
  };

  const getRequestParams = () => {
    const hasExtraCust = extraCustomerCodesRef.current?.length > 0;
    const hasExtraItem = extraItemCodesRef.current?.length > 0;
    const custFilterList = (customerSearch.current !== "" || hasExtraCust) ? [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean) : filterValue?.customerHrchys?.map(data => data?.hrchyCd);
    const categoryFilterList = (itemSearch.current !== "" || hasExtraItem) ? [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean) : filterValue?.category?.map(data => data?.hrchyCd);
    const params = {};
    const searchFilters = {
      planVersionId: selectVersion?.id ?? reduxViewState?.versionId ?? null,
      custLevelCd: (customerSearch.current !== "" || hasExtraCust) ? customer.cd : getLevelCdOnParams(true),
      itemLevelCd: (itemSearch.current !== "" || hasExtraItem) ? item.cd : getLevelCdOnParams(false),
      customerFilter: isEmpty(custFilterList) ? null : custFilterList,
      itemFilter: isEmpty(categoryFilterList) ? null : categoryFilterList,
      errorFilter: isEmpty(brushValue) ? null : brushValue,
      bestMetric: metricRef.current !== "" ? metricRef.current : "WAPE"
    };

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    params.searchFilters = searchFilters;
    return { ...params, ...sortParams };
  };

  const retrieveLeaderboard = () => {
    onBtShowLoading();
    setMetric("");
    const params = getRequestParams();
    params.searchFilters.drawChart = "false";
    bfLeaderboardService.getLeaderboard(params)
      .then(response => {
        if (response?.data?.message) {
          const { message } = response.data;
          setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          return;
        }
        const { result, totalPages } = response.data;

        if (isEmpty(result)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoBFErrorData") });
        }
        setTotalPages(totalPages);
        const transformed = result.map(row => ({
          ...row,
          rmse: row.rmse === -1 || row.rmse === null ? null : row.rmse,
          mape: row.mape === -1 || row.mape === null ? null : row.mape,
          wape: row.wape === -1 || row.wape === null ? null : row.wape,
        }));

        setLeaderboard(transformed);

      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {
          currentPage,
          pageSize,
          selectVersion,
          salesPeriod,
          brushValue,
          filter: filterValue,
          itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current,
          customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current,
          advancedFilters
        });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
        onBtHide();
      })
  };

  const drawChart = (histogram) => {
    const params = getRequestParams();
    params.searchFilters.drawChart = "true";

    bfLeaderboardService.getLeaderboard(params)
      .then(response => {
        if (response?.data?.message) {
          return;
        }
        const { bestModel, wapeBinning } = response.data;

        const transformedModel = Object.entries(bestModel).map(([modelName, count]) => ({
          modelName,
          count
        }));
        transformedModel.sort((b, a) => a.count - b.count);
        setBestModelCnt(transformedModel);
        if (histogram) {
          const transformedError = Object.entries(wapeBinning).map(([bucketStart, frequency]) => ({
            bucketStart,
            frequency
          }));
          transformedError.sort((a, b) => a.bucketStart - b.bucketStart);
          setForecastError(transformedError);
        }
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      })

  }

  const drawHistoryChart = (cust, item) => {
    const params = {
      planVersionId: selectVersion?.id ?? reduxViewState?.versionId ?? null,
      custLevelCd: cust,
      itemLevelCd: item,
      customerFilter: filterValue?.customerHrchys?.map(data => data?.hrchyCd) ?? null,
      itemFilter: filterValue?.category?.map(data => data?.hrchyCd) ?? null,
    }

    if (!isEmpty(chart1Data) && chart1Data?.cust === cust && chart1Data?.item === item) {
      const slicedChartData = chart1Data.slice(-salesPeriod);
      setChartSeriesData(slicedChartData, chart1ModelCds, codeUtil.toggleType.BAR);
    } else {
      setChartLoading(true);
      bfLeaderboardService.getLeaderboardHistorySales(params)
        .then(response => {
          const { fcstHist, histSales, posActiveFlg } = response.data;
          let salesData = [];
          let fcstData = [];
          let mergedData = [];

          if (!isUndefined(histSales)) {
            setPosActiveFlg(posActiveFlg);
            const histSalesData = posActiveFlg ? histSales.posSalesActualQty : histSales.salesActualQty;
            salesData = Object.entries(histSalesData).map(([txnDate, qty]) => ({
              txnDate,
              qty: qty === null ? 0 : qty
            }));
            salesData.sort((a, b) => new ZDate(a.txnDate) - new ZDate(b.txnDate));
          }

          if (!isUndefined(fcstHist)) {
            // fcstHist 데이터에서 txnDate, modelCd, fcst만 추출
            fcstData = fcstHist.reduce((acc, item) => {
              const existingModelData = acc.find(data => data.modelName === item.modelName);
              if (existingModelData) {
                existingModelData[item.txnDate] = item.fcst;
              } else {
                const newModelData = {
                  modelName: item.modelName,
                  [item.txnDate]: item.fcst,
                };
                acc.push(newModelData);
              }
              return acc;
            }, []);
          }

          // salesData와 모든 모델의 데이터를 병합
          mergedData = salesData.map(item => {
            const modelData = fcstData.reduce((acc, model) => {
              acc[model.modelName] = model[item.txnDate];
              return acc;
            }, {});
            return { ...item, ...modelData };
          });

          mergedData.cust = cust;
          mergedData.item = item;
          setChart1Data(mergedData);
          maxSalesPeriod.current = mergedData?.length || 36;
          const slicedChartData = mergedData.slice(-salesPeriod);

          if (isEmpty(salesData)) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoSalesData") });
          }

          if (isEmpty(fcstData)) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoPrevBFData") });
          }

          const modelCds = fcstData.map(entry => entry.modelName);
          setChart1ModelCds(modelCds);
          setChartSeriesData(slicedChartData, modelCds, codeUtil.toggleType.BAR);

        })
        .catch(e => {
          debug(e);
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        })
        .finally(() => {
          setChartLoading(false);
        })
    }
  };

  const columnDefs = [
    {
      headerName: "customerCd",
      showRowGroup: "customerCd",
      colId: "customerCdGrp",
      pinned: "left", lockPinned: true,
      width: 200,
      cellRenderer: "agGroupCellRenderer",
      type: ["nonEditableColumn"],
      cellClass: "stringType",
      cellRendererParams: { suppressCount: true },
      editable: false,
      filterType: "string"
    },
    {
      headerName: "customerName",
      showRowGroup: "customerNm",
      colId: "customerNmGrp",
      chartDataType: "category",
      width: 200,
      cellRenderer: "agGroupCellRenderer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      cellRendererParams: { suppressCount: true },
      editable: false,
      filterType: "string"
    },
    {
      headerName: "itemCd",
      showRowGroup: "itemCd",
      colId: "itemCdGrp",
      chartDataType: "category",
      width: 200,
      cellRenderer: "agGroupCellRenderer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      cellRendererParams: { suppressCount: true },
      editable: false,
      filterType: "string"
    },
    {
      headerName: "itemName",
      showRowGroup: "itemNm",
      colId: "itemNmGrp",
      chartDataType: "category",
      width: 200,
      cellRenderer: "agGroupCellRenderer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      cellRendererParams: { suppressCount: true },
      editable: false,
      filterType: "string"
    },
    { headerName: "customerCd", field: "customerCd", hide: true, suppressColumnsToolPanel: true, rowGroup: true },
    { headerName: "customerName", field: "customerNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true },
    { headerName: "itemCd", field: "itemCd", hide: true, suppressColumnsToolPanel: true, rowGroup: true },
    { headerName: "itemNm", field: "itemNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true },
    { headerName: "forecastMethod", field: "modelName", sortable: false, hide: false, suppressColumnsToolPanel: true, rowGroup: false, filterType: "string", filterOption: algorithmType },
    { headerName: "rank", field: "rankNum", sortable: false, hide: false, suppressColumnsToolPanel: true, rowGroup: false, filterType: "number" }
  ];

  const exportColumnDefs = [
    { headerName: "customerCd", field: "customerCd", cellClass: 'stringType' },
    { headerName: "customerName", field: "customerNm" },
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm" },
    { headerName: "forecastMethod", field: "modelName" },
    { headerName: "rank", field: "rankNum" }
  ]

  const getRowStyle = params => {
    if (params?.data?.rankNum && params.data.rankNum === 1) {
      return { background: green[50] };
    }
    return null;
  };

  // function brushOnChange(e) {
  //   setBrushValue([parseFloat(forecastError[e.startIndex]?.bucketStart), parseFloat(forecastError[e.endIndex]?.bucketStart)]);
  // }

  const onCellGridClicked = e => {
    const filterInfo = extractFilterInfo(e, t, gridRef);
    getFilterStore(viewName).setValue(filterInfo);

    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    setSelectedRows(selected[0].data);
    e?.api?.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
  };

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setSelectedRows(selectedRows[0]);
    }
  };

  const onChangeSalesPeriod = event => {
    const value = Number(event.target.value);
    setSalesPeriod(value);
  }

  const initPageToRetrieve = () => {
    clearChart();
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveLeaderboard();
      drawChart(true);
    }
  }

  const initCheck = params => {
    if (leaderboard) {
      if (params.type === "componentStateChanged" && params?.rowData) {
        let firstLeafNode = null;
        params.api.forEachLeafNode(node => { if (!firstLeafNode) firstLeafNode = node; });
        if (firstLeafNode) {
          const targetNodes = firstLeafNode?.parent?.allLeafChildren ?? [];
          params.api.setNodesSelected({ nodes: targetNodes, newValue: true, source: CHECKBOX_SELECTED });
        }
      }
    }
  };

  const tooltipFormatter = (value, name, toolTipProps) => {
    return [value, t(name), toolTipProps];
  }

  const histogramBarClick = (e, index) => {
    const diffValue = forecastError[1]?.bucketStart - forecastError[0]?.bucketStart;

    if (selectedBarIndex !== null && selectedBarIndex === index) {
      setBrushValue([parseFloat(forecastError[0]?.bucketStart), parseFloat(forecastError[forecastError.length - 1]?.bucketStart)]);
      setSelectedBarIndex(null);
    } else {
      if (index + 1 <= forecastError.length) {
        setBrushValue([parseFloat(forecastError[index]?.bucketStart), parseFloat(forecastError[index + 1]?.bucketStart)]);
      } else {
        setBrushValue([parseFloat(forecastError[index]?.bucketStart), parseFloat(forecastError[forecastError.length - 1]?.bucketStart + diffValue)]);
      }
      setSelectedBarIndex(index);
    }
  }

  const onBtShowLoading = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', true);
  }, [])

  const onBtHide = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', false);
  }, [])

  const onGridReady = () => {
    setGridLoading(true);
  }

  useEffect(() => {
    if (gridLoading) {
      GridUtils.setColumnDefs({
        ...gridRef.current,
        columnDefs: [...columnDefs, ...merticColDef],
        viewName,
        initState: true
      });
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, merticColDef, gridLoading]);

  useEffect(() => {
    if (gridLoading && !isEmpty(selectVersion)) {
      clearChart();
      retrieveLeaderboard();
      drawChart(false);
    }
    return () => {
      setLeaderboard([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(selectVersion)) {
      retrieveParameters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectVersion])

  useEffect(() => {
    if (!isEmpty(selectVersion) && gridLoading) {
      initPageToRetrieve();
    }
    return () => {
      setLeaderboard([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(selectVersion) && !isEqual(reduxViewState?.brushValue, brushValue)) {
      if (currentPage > 1) {
        setCurrentPage(1);
      } else {
        retrieveLeaderboard();
        drawChart(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brushValue])

  useEffect(() => {
    if (metric !== "" && gridLoading) {
      makeMetricColDef(metric);
      initPageToRetrieve();
    }
    return () => {
      setLeaderboard([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric])

  useEffect(() => {
    if (!isEmpty(selectedRows)) {
      drawHistoryChart(selectedRows?.customerCd, selectedRows?.itemCd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows])

  useEffect(() => {
    if (salesPeriod > maxSalesPeriod.current) {
      setSalesPeriod(maxSalesPeriod.current)
    } else if (salesPeriod < MIN_SALES_PERIOD) {
      setSalesPeriod(MIN_SALES_PERIOD);
    } else if (!isEmpty(chart1Data) && !isEmpty(selectedRows)) {
      const reduxState = reduxUtil.createObjectState(viewName, { salesPeriod });
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
      drawHistoryChart(selectedRows.customerCd, selectedRows.itemCd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesPeriod])

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { chartToggle });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartToggle])

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboard, viewName]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    retrieveMeasureDisplayName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <>
      <FilterContainer>
        <DpVersionSelect
          selectedVersion={setSelectVersion}
          reduxVersion={reduxViewState?.selectVersion ?? null}
          reload={vListReload}
          setReload={setVListReload}
          status={["RELEASED", "SCENARIO"]}
          isMain
        />
        <CustomerHrchyTreeFilter
          viewName={viewName}
          onChangeFilter={setFilterValue}
          defaultValue={defaultValue}
          enableSelectParent
          enableMultiSelect
          treeload
          styles={{ width: 290 }}
        />
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} sx={{ mr: 1.5 }} extraCodesRef={extraCustomerCodesRef} />
        <ItemHrchyTreeFilter
          viewName={viewName}
          onChangeFilter={setFilterValue}
          defaultValue={itemHrchyDefaultValue}
          enableSelectParent
          enableMultiSelect
          treeload
          styles={{ width: 280 }}
        />
        <DPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton
            title={title}
            service={bfLeaderboardService}
            params={{ ...getRequestParams() }}
            columnDefs={[...exportColumnDefs, ...merticColDef]}
            isCsv
            translationColumns={["forecastMethod"]}
          />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box sx={{ display: "flex", height: "100%" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <SplitterContainer initialSizes={[0.7, 1]} collapseDirection="up">
          <Box id="bf-leaderboard-chart" className="chart-grid-area chart-area" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "stretch",
                px: 2,
                py: 0,
                position: "relative",
              }}
            >
              {/* 좌측 조건 필터 (조건부 렌더링) */}
              <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", height: "100%" }}>
                {chartToggle === HISTORY && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.85)",
                      // boxShadow: 2,
                      height: "100%",
                      px: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t("salesPeriod")}
                    </Typography>
                    <FormControl sx={{ width: "130px" }}>
                      <TextField
                        size="small"
                        value={salesPeriod}
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{t("bucket")}</InputAdornment>
                        }}
                        inputProps={{ min: MIN_SALES_PERIOD, max: maxSalesPeriod.current, step: 1 }}
                        onChange={onChangeSalesPeriod}
                      />
                    </FormControl>
                  </Box>
                )}
              </Box>

              {/* 우측 탭 토글 버튼 */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <BFToggleComponent chartToggle={chartToggle} setChartToggle={setChartToggle} />
              </Box>
            </Box>
            <Divider sx={{ mt: 1}}/>
            <Box
              sx={{
                flex: 1,
                p: 2,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0
              }}
            >
              {/* HISTORY VIEW */}
              {chartToggle === HISTORY && (
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {chartLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <CircularProgress />
                        </Box>
                      ) : (
                        <ChartComposed
                          xAxis={["txnDate"]}
                          data={chartData}
                          dataSeries={chartSeries}
                          tooltipAnimation={false}
                          options={{
                            margin: { left: 30, top: 20, bottom: 15, right: 30 },
                            xAxisHeight: 30,
                            minHeight: "150px"
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="txnDate" fontSize={FS.BODY} />
                          <YAxis fontSize={FS.BODY} />
                        </ChartComposed>
                      )}
                    </ResponsiveContainer>
                  </Box>
              )}

              {/* HISTOGRAM + BEST VIEW */}
              {chartToggle === HISTOGRAM_BEST && (
                <Box sx={{ display: "flex", flex: 1, gap: 2, minHeight: 0 }}>
                  <Box sx={{ width: "50%" }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t("historyErrorHistogram", { N: metricRef.current })}
                    </Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={forecastError} barSize={30} margin={{ left: 10, top: 5, bottom: 45, right: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucketStart" fontSize={FS.H2} />
                        <YAxis fontSize={FS.H2} />
                        <Tooltip isAnimationActive={false} formatter={tooltipFormatter} content={<AgStyleTooltipContent />} />
                        <ReferenceLine y={0} stroke="#000" />
                        {forecastError?.length > 0 && (
                          <Legend layout="horizontal" height={30} verticalAlign="bottom" align="center" wrapperStyle={{ marginLeft: "30px" }} formatter={t} />
                        )}
                        <Bar dataKey="frequency" name="frequency" fill={colorSet(0)} onClick={histogramBarClick}>
                          {forecastError.map((entry, index) => (
                              // eslint-disable-next-line react/no-array-index-key
                            <Cell key={`cell-${index}`} fill={index === selectedBarIndex ? colorSet(1) : colorSet(0)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <Box sx={{ width: "50%" }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{t("bestSelectCnt")}</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bestModelCnt} margin={{ left: 10, top: 5, bottom: 45, right: 10 }}>
                        <CartesianGrid strokeDasharray="6 6" />
                        <XAxis dataKey="modelName" fontSize={FS.BODY} interval={0} angle={-10} dy={10} />
                        <YAxis fontSize={FS.H2} />
                        <Tooltip isAnimationActive={false} formatter={tooltipFormatter} content={<AgStyleTooltipContent />} />
                        {bestModelCnt?.length > 0 && (
                          <Legend layout="horizontal" height={30} verticalAlign="bottom" align="center" wrapperStyle={{ marginLeft: "30px" }} formatter={t} />
                        )}
                        <ReferenceLine y={0} stroke="#000" />
                        <Bar dataKey="count" name="count" fill={colorSet(0)}>
                          {bestModelCnt.map((entry) => (
                            <Cell key={entry.modelName} fill={algorithmColor[entry.modelName]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Box className="ag-theme-balham grid-area" sx={{ mt: 2 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: "calc(100% - 60px)", mr: 1.5 }}>
            <Box sx={{ flexGrow: 1 }}>
              <AgGridReact
                ref={gridRef}
                rowData={leaderboard}
                {...defaultGridMemo}
                rowSelection={{
                  ...defaultGridMemo.rowSelection,
                  groupSelects: "descendants",
                }}
                getRowStyle={getRowStyle}
                onGridReady={onGridReady}
                onCellClicked={onCellGridClicked}
                onRowSelected={onRowSelected}
                suppressAutoSize
                groupHideOpenParents
                // suppressRowClickSelection
                overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
                animateRows
                statusBar={statusBar}
                groupDisplayType={"custom"}
                groupDefaultExpanded={-1}
                onComponentStateChanged={initCheck}
                onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
              />
            </Box>
            </Box>
            <Box mb={1} />
            <PaginationContainer
              currentPage={currentPage} totalPages={tPages}
              pageSize={pageSize} pageSizes={pageSizes}
              setCurrentPage={setCurrentPage} setPageSize={setPageSize}
            />
          </Box>
        </SplitterContainer>
      </Box>
      {openFilter && (
        <>
          <ResizableFilterPanel>
            <AdvancedFilter
              columnDefs={columnDefs}
              advancedFilters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        </>
      )}
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

const BFToggleComponent = ({ chartToggle, setChartToggle, boxSx }) => {
  const { t } = useTranslation();

  const buttonSx = { padding: "6px 12px", minWidth: "150px" };

  const toggleHandler = (_, newAlignment) => {
    if (newAlignment) {
      setChartToggle(newAlignment);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", ...boxSx }}>
      <ToggleButtonGroup
        value={chartToggle}
        exclusive
        onChange={toggleHandler}
        aria-label="chart toggle"
      >
        <StyledToggleButton sx={buttonSx} value={HISTORY}>
          {t("historySalesAndFcst")}
        </StyledToggleButton>
        <StyledToggleButton sx={buttonSx} value={HISTOGRAM_BEST}>
          {t("fcstPerfAnalysis")}
        </StyledToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default withTranslation()(BfLeaderboard);